import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import Select from "react-select";

// Services & Types
import { AuditService } from "services/audit.service";
import UserService from "services/user.service";
import {
  IAuditLogResponseDTO,
  IUser,
  AUDIT_ACTION_LABELS,
  AUDIT_STATUS_LABELS,
} from "models/church.types";

export default function AuditLogList() {
  const location = useLocation();
  const history = useHistory();

  // Lecture du filtre utilisateur depuis l'URL pour la synchronisation
  const queryParams = new URLSearchParams(location.search);
  const userIdFromUrl = queryParams.get("userId");

  const [logs, setLogs] = useState<IAuditLogResponseDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [userOptions, setUserOptions] = useState<
    { value: string; label: string }[]
  >([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // 1. Chargement du catalogue des utilisateurs pour le filtre
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await UserService.getAll(0, 500);
        const options = response.data.map((u: IUser) => ({
          value: u.login,
          label:
            u.firstName && u.lastName
              ? `${u.firstName} ${u.lastName} (${u.login})`
              : u.login,
        }));
        setUserOptions(options);
      } catch (err) {
        console.error("Erreur chargement des utilisateurs:", err);
      }
    };
    loadUsers();
  }, []);

  // 2. Fonction de chargement des logs
  const loadData = useCallback(
    async (page: number, userId: string | null) => {
      setLoading(true);
      try {
        const result = await AuditService.getAuditLogs(
          page,
          itemsPerPage,
          userId || undefined,
        );
        setLogs(result.data);
        setTotalItems(result.totalCount);
      } catch (error) {
        console.error("Erreur chargement des logs d'audit:", error);
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage],
  );

  // 3. Gestion du changement de filtre utilisateur via URL
  const handleUserChange = (selected: any) => {
    const newUserLogin = selected ? selected.value : null;
    const params = new URLSearchParams(location.search);

    if (newUserLogin) params.set("userId", newUserLogin);
    else params.delete("userId");

    history.push({ search: params.toString() });
    setCurrentPage(0); // Reset pagination au changement de filtre
  };

  // 4. Effet de déclenchement du chargement
  useEffect(() => {
    loadData(currentPage, userIdFromUrl);
  }, [currentPage, userIdFromUrl, loadData]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Styles pour React Select (cohérent avec votre charte graphique)
  const customStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: "#1e293b",
      borderColor: "#334155",
      color: "white",
      minHeight: "38px",
      height: "38px",
      borderRadius: "0.5rem",
      boxShadow: "none",
      fontFamily: "ui-sans-serif, system-ui, sans-serif",
      "&:hover": { borderColor: "#475569" },
    }),
    singleValue: (base: any) => ({ ...base, color: "white" }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1e293b",
      border: "1px solid #334155",
      zIndex: 9999,
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#334155" : "transparent",
      color: "white",
      cursor: "pointer",
    }),
  };

  return (
    <div className="flex flex-col w-full min-h-screen mt-3 pt-24 justify-start px-4">
      {/* HEADER & FILTRES */}
      <div className="flex flex-col lg:flex-row items-center gap-4 mb-8">
        <h2 className="text-[#D4AF37] text-xl font-bold mr-auto">
          <i className="fas fa-history mr-2 text-[#D4AF37]"></i>
          Pistes d'Audit Système
        </h2>

        <div className="w-full lg:w-80">
          <Select
            options={userOptions}
            styles={customStyles}
            placeholder="Filtrer par utilisateur..."
            isClearable
            value={userOptions.find((o) => o.value === userIdFromUrl) || null}
            onChange={handleUserChange}
            menuPortalTarget={document.body}
          />
        </div>
      </div>

      {/* TABLEAU DES LOGS */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">Date & Heure</th>
                <th className="px-6 py-5">Utilisateur</th>
                <th className="px-6 py-5">Type d'Action</th>
                <th className="px-6 py-5">Description</th>
                <th className="px-6 py-5">Adresse IP / Navigateur</th>
                <th className="px-6 py-5 text-center">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-400">
                    <i className="fas fa-circle-notch animate-spin mr-2"></i>
                    Récupération des traces...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-clipboard-list block text-3xl mb-3 opacity-20"></i>
                    Aucun log d'audit enregistré pour ces critères.
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const actionMeta = AUDIT_ACTION_LABELS[log.actionType] || {
                    label: log.actionType,
                    colorClass:
                      "bg-slate-500/10 text-slate-400 border-slate-700",
                  };
                  const statusMeta = AUDIT_STATUS_LABELS[log.status];

                  return (
                    <tr
                      key={log.id}
                      className="hover:bg-slate-700/40 transition-colors group"
                    >
                      {/* 1. Date & Heure */}
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-slate-400">
                        {log.actionDate
                          ? new Date(log.actionDate).toLocaleString("fr-FR")
                          : "—"}
                      </td>

                      {/* 2. Utilisateur */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-bold text-white uppercase tracking-tight">
                          {log.fullName || log.userId}
                        </div>
                        {log.fullName && (
                          <div className="text-[10px] text-slate-500 font-mono">
                            ID: {log.userId}
                          </div>
                        )}
                      </td>

                      {/* 3. Type d'Action (Badge mappé) */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2.5 py-1 rounded-lg border text-[10px] font-bold uppercase tracking-wide ${actionMeta.colorClass}`}
                        >
                          {actionMeta.label}
                        </span>
                      </td>

                      {/* 4. Description de l'action */}
                      <td
                        className="px-6 py-4 text-xs max-w-xs md:max-w-sm block overflow-hidden text-ellipsis whitespace-nowrap text-slate-300"
                        title={log.description}
                      >
                        {log.description || "—"}
                      </td>

                      {/* 5. Métadonnées Réseau */}
                      <td className="px-6 py-4 text-xs">
                        <div className="font-mono text-slate-400">
                          {log.ipAddress || "IP Inconnue"}
                        </div>
                        <div
                          className="text-[10px] text-slate-500 max-w-[150px] truncate"
                          title={log.browser || undefined}
                        >
                          {log.browser || "—"}
                        </div>
                      </td>

                      {/* 6. Statut (Badge Icone mappé) */}
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[10px] font-bold ${statusMeta?.colorClass}`}
                        >
                          <i className={statusMeta?.icon}></i>
                          {statusMeta?.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total : <span className="text-[#D4AF37]">{totalItems}</span> traces
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0 || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
